import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { FormSubmitButton } from '../../../../components/Button/FormSubmitButton';
import { FormComponent } from '../../../../components/FormCom/FormComponent';
import ImageUploadField from '../../../../components/FormCom/ImageUploadField';
import useBusinessInfo from '../../../../hooks/dashboard/settings/useBusinessInfo';
import useCompanyProfileImageUpload from '../../../../hooks/dashboard/settings/useCompanyProfileImageUpload';
import {
  UploadCompanyProfileImageInitialValues,
  UploadCompanyProfileImageSchema,
} from '../../../../models/dashboard/settings/upload-company-profile-img';
import { useAppSelector } from '../../../../redux/store';

const ProfilePictureArea = () => {
  const { businessInfo } = useAppSelector((state) => state.businessInfoData);
  const { businessInfo: businessInfoHook } = useBusinessInfo();
  const { uploadCompanyProfileImage, loading } = useCompanyProfileImageUpload();
  const [defaultImgUrl, setDefaultImgUrl] = useState<string | null>(
    businessInfo?.profilePicture ?? null,
  );

  useEffect(() => {
    setDefaultImgUrl(businessInfo?.profilePicture ?? null);
  }, [businessInfo?.profilePicture]);

  const handleOnSubmit = (values: any, actions: any) => {
    uploadCompanyProfileImage(values, () => {
      actions.resetForm(UploadCompanyProfileImageInitialValues);
      businessInfoHook();
    });
  };

  return (
    <FormHeader>
      <FormHeaderLabel>Profile Settings</FormHeaderLabel>
      <ProfileFormArea>
        <LabelArea>Profile Picture</LabelArea>

        <FormComponent
          initialValues={UploadCompanyProfileImageInitialValues}
          schema={UploadCompanyProfileImageSchema}
          onSubmit={handleOnSubmit}
          className={'inline space-between'}
        >
          <ImageUploadField
            id='profilePicture'
            name='profilePicture'
            defaultImgUrl={defaultImgUrl}
            setDefaultImgUrl={setDefaultImgUrl}
            title={
              defaultImgUrl
                ? 'Update Company Profile Picture'
                : 'Upload Company Profile Picture'
            }
          />
          <FormSubmitButton loading={loading} className='fit flex-end'>
            <ProfilePictureSubmitLabel
              hasExistingPicture={!!businessInfo?.profilePicture}
            />
          </FormSubmitButton>
        </FormComponent>
      </ProfileFormArea>
    </FormHeader>
  );
};

export default ProfilePictureArea;

// Reads the live Formik value so the label only flips to 'Save Changes' once a new file is actually selected.
const ProfilePictureSubmitLabel = ({
  hasExistingPicture,
}: {
  hasExistingPicture: boolean;
}) => {
  const { values } = useFormikContext<{ profilePicture: File | null }>();

  if (values.profilePicture) {
    return <>Save Changes</>;
  }

  return (
    <>
      {hasExistingPicture ? 'Edit Profile Picture' : 'Upload Profile Picture'}
    </>
  );
};

const ProfileFormArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 24px;
  border-radius: 8px;

  @media (max-width: 884px) {
    display: none;
  }
`;

const LabelArea = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 30px;
`;

const FormHeader = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`;

const FormHeaderLabel = styled.div`
  display: flex;
  font-size: 22px;
  line-height: 22px;
  font-weight: 600;
`;
